/**
 * Klaviyo Web Push Notification Service
 * Handles subscription management and integration with Klaviyo
 */

interface PushSubscriptionJSON {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

interface KlaviyoSubscriptionData {
  endpoint: string;
  auth: string;
  p256dh: string;
}

const KLAVIYO_SW_NAME = "klaviyo/klaviyo-notification-sw.js";
const SW_REGISTRATION_KEY = "klaviyo_sw_registered";

export class KlaviyoService {
  /**
   * Register the Klaviyo service worker
   */
  // Proposed flow for Klaviyo
  static async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    // 1. Check for existing registration at /klaviyo/ scope
    let registration =
      await navigator.serviceWorker.getRegistration("/klaviyo/");

    // 2. Verify it's the correct service worker
    if (registration) {
      const scriptURL =
        registration.active?.scriptURL ||
        registration.installing?.scriptURL ||
        registration.waiting?.scriptURL;

      if (!scriptURL?.includes("klaviyo-notification-sw.js")) {
        await registration.unregister();
        registration = undefined;
      }
    }
    console.log("registration", registration);
    // 3. Only register if needed
    if (!registration) {
      registration = await navigator.serviceWorker.register(
        `/${KLAVIYO_SW_NAME}`,
        { scope: "/klaviyo/" },
      );
    }

    return registration;
  }

  /**
   * Subscribe to push notifications
   * @param publicVapidKey - VAPID key for push subscription
   * @param accountId - User's account ID to send as external_id to Klaviyo
   */
  static async subscribeToPush(
    publicVapidKey: string,
    accountId?: string,
    email?: string,
  ): Promise<void> {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      throw new Error("Push notifications are not supported");
    }

    try {
      console.log("Starting push subscription process...");
      if (accountId) {
        console.log("Account ID provided:", accountId);
      }

      // Check if VAPID key is valid
      if (!publicVapidKey || publicVapidKey.length < 10) {
        throw new Error("Invalid VAPID key provided");
      }
      console.log("VAPID key validated");

      // Register or get existing registration
      console.log("Registering/getting service worker...");
      const registration = await this.registerServiceWorker();
      console.log("Service worker ready:", registration);

      // Unsubscribe existing subscription first to get fresh credentials.
      // This prevents stale/expired endpoints from persisting in Klaviyo.
      const existingSubscription =
        await registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log("[Klaviyo] Refreshing existing subscription...");
        await existingSubscription.unsubscribe();
      }

      // Create new subscription with fresh endpoint and keys
      console.log("[Klaviyo] Creating new push subscription...");
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(publicVapidKey),
      });
      console.log("Push subscription created:", subscription.endpoint);

      // Extract and send data
      console.log("Extracting subscription data...");
      const subscriptionData = this.extractSubscriptionData(subscription);
      console.log("Subscription data extracted");

      console.log("Sending subscription to Klaviyo...");
      await this.sendSubscriptionToKlaviyo(
        subscriptionData,
        subscription.endpoint,
        accountId,
        email,
      );
      console.log("Subscription data sent to Klaviyo");

      console.log("Successfully subscribed to push notifications");
    } catch (error) {
      console.error("Error subscribing to push notifications:", error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  static async unsubscribeFromPush(): Promise<void> {
    try {
      const registration = await this.registerServiceWorker();
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        console.log("Successfully unsubscribed from push notifications");
      }
    } catch (error) {
      console.error("Error unsubscribing from push notifications:", error);
      throw error;
    }
  }

  /**
   * Check if user is subscribed to push notifications
   */
  static async isSubscribed(): Promise<boolean> {
    try {
      (window as any).klaviyo.isIdentified().then((identified: any) => {
        console.log("identified", identified);
      });
      const registration = await this.registerServiceWorker();
      const subscription = await registration.pushManager.getSubscription();
      return subscription !== null;
    } catch (error) {
      console.error("Error checking subscription status:", error);
      return false;
    }
  }

  /**
   * Extract subscription data from PushSubscription object
   */
  private static extractSubscriptionData(
    subscription: PushSubscription,
  ): KlaviyoSubscriptionData {
    const json = subscription.toJSON() as PushSubscriptionJSON;

    if (!json.keys || !json.keys.p256dh || !json.keys.auth) {
      throw new Error("Push subscription keys are missing");
    }

    return {
      endpoint: json.endpoint,
      auth: json.keys.auth,
      p256dh: json.keys.p256dh,
    };
  }

  /**
   * Send subscription data to Klaviyo
   * This integrates with Klaviyo's web push tracking
   * Sends accountId as $external_id to create/link user profile
   */
  private static async sendSubscriptionToKlaviyo(
    subscriptionData: KlaviyoSubscriptionData,
    endpoint: string,
    accountId?: string,
    email?: string,
  ): Promise<void> {
    // Verify Klaviyo is available
    if (typeof (window as any).klaviyo === "undefined") {
      console.warn("Klaviyo is not loaded, attempting to wait...");
      // Wait up to 5 seconds for Klaviyo to load
      let attempts = 0;
      while (attempts < 50 && typeof (window as any).klaviyo === "undefined") {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      if (typeof (window as any).klaviyo === "undefined") {
        throw new Error("Klaviyo failed to load within timeout period");
      }
    }

    try {
      console.log(email);
      console.log("Klaviyo object available, sending subscription data...");

      // Track the subscription event with external_id if provided
      // This creates/updates user profile in Klaviyo
      console.log("Tracking subscription event...");

      const eventProperties: any = {
        url: window.location.href,
        web_push__endpoint: endpoint,
        web_push__auth: subscriptionData.auth,
        web_push__p256dh: subscriptionData.p256dh,
        timestamp: new Date().toISOString(),
        subscription_source: "post_login_popup",
      };

      // Add external_id if accountId is provide
      if (accountId) {
        eventProperties.$external_id = accountId;
      }
      console.log("eventProperties", eventProperties);
      (window as any).klaviyo.identify({
        "Web Push Notification": "Subscribed",
        $email: email,
        $external_id: accountId,
        web_push__endpoint: endpoint,
        web_push__auth: subscriptionData.auth,
        web_push__p256dh: subscriptionData.p256dh,
      });
      const identified = await (window as any).klaviyo.isIdentified();
      console.log(
        "🚀 ~ KlaviyoService ~ sendSubscriptionToKlaviyo ~ identified:",
        identified,
      );
      (window as any).klaviyo.push([
        "track",
        "Subscribed to Web Push",
        eventProperties,
      ]);

      console.log(
        `Subscription data sent to Klaviyo ${accountId ? "with external_id: " + accountId : "(anonymous tracking)"}`,
      );
    } catch (error) {
      console.error("Error sending subscription to Klaviyo:", error);
      throw error;
    }
  } /**
   * Track unsubscription event in Klaviyo
   */
  static trackUnsubscription(): void {
    if (typeof (window as any).klaviyo === "undefined") {
      console.warn("Klaviyo is not loaded");
      return;
    }

    try {
      (window as any).klaviyo.push([
        "track",
        "Unsubscribed from Web Push",
        {
          url: window.location.href,
          timestamp: new Date().toISOString(),
        },
      ]);

      console.log("Unsubscription tracked in Klaviyo");
    } catch (error) {
      console.error("Error tracking unsubscription:", error);
    }
  }

  /**
   * Convert base64 VAPID key to Uint8Array
   */
  private static urlBase64ToUint8Array(base64String: string): ArrayBuffer {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, "+")
      .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray.buffer;
  } /**
   * Get current service worker registration status
   */
  static async getRegistrationStatus(): Promise<ServiceWorkerRegistration | null> {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      return (
        registrations.find(
          (reg) =>
            reg.scope.includes("/") &&
            reg.active?.scriptURL.includes(KLAVIYO_SW_NAME),
        ) || null
      );
    } catch (error) {
      console.error("Error getting registration status:", error);
      return null;
    }
  }
}
